import { isPushConfigured, isNotificationSupported, getMessagingIfSupported } from '@/lib/messaging'

export type PushBlockReason =
  | 'ios_chrome'
  | 'ios_other_browser'
  | 'ios_safari_browser'
  | 'not_configured'
  | 'unsupported'

export type PushPlatformInfo = {
  canEnablePush: boolean
  reason: PushBlockReason | null
  /** Real-time toast alerts while admin panel is open (works on iOS). */
  inAppAlertsAvailable: boolean
  isStandalonePwa: boolean
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/** Chrome on iOS — uses WebKit; Apple does not expose web push here. */
export function isIosChrome(): boolean {
  return isIosDevice() && /CriOS/i.test(navigator.userAgent)
}

/** Firefox, Edge, etc. on iOS — same WebKit limitation as Chrome. */
export function isIosNonSafariBrowser(): boolean {
  if (!isIosDevice()) return false
  return /CriOS|FxiOS|EdgiOS|OPiOS/i.test(navigator.userAgent)
}

export function isIosSafari(): boolean {
  if (!isIosDevice() || isIosNonSafariBrowser()) return false
  return /Safari/i.test(navigator.userAgent) && /AppleWebKit/i.test(navigator.userAgent)
}

/** Launched from iOS home screen (required for Safari web push). */
export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function pushBlockMessage(reason: PushBlockReason): string {
  switch (reason) {
    case 'ios_chrome':
      return (
        'Chrome on iPhone cannot enable web push (Apple restriction). ' +
        'Open the admin site in Safari → Share → Add to Home Screen → open that icon → tap the bell to enable alerts.'
      )
    case 'ios_other_browser':
      return (
        'This browser on iPhone cannot receive web push. ' +
        'Use Safari, add the site to your Home Screen, then enable alerts from the home screen app.'
      )
    case 'ios_safari_browser':
      return (
        'On iPhone, push alerts work from the Home Screen app only. ' +
        'In Safari tap Share → Add to Home Screen, open that icon, log in to admin, then tap the bell.'
      )
    case 'not_configured':
      return 'Push is not configured on the server (VITE_FIREBASE_VAPID_KEY).'
    default:
      return 'Push notifications are not supported in this browser.'
  }
}

export async function getPushPlatformInfo(): Promise<PushPlatformInfo> {
  const inAppAlertsAvailable = true
  const standalone = isStandalonePwa()

  if (!isPushConfigured()) {
    return {
      canEnablePush: false,
      reason: 'not_configured',
      inAppAlertsAvailable,
      isStandalonePwa: standalone,
    }
  }

  if (isIosChrome()) {
    return {
      canEnablePush: false,
      reason: 'ios_chrome',
      inAppAlertsAvailable,
      isStandalonePwa: standalone,
    }
  }

  if (isIosNonSafariBrowser() && !isIosChrome()) {
    return {
      canEnablePush: false,
      reason: 'ios_other_browser',
      inAppAlertsAvailable,
      isStandalonePwa: standalone,
    }
  }

  if (isIosSafari() && !standalone) {
    return {
      canEnablePush: false,
      reason: 'ios_safari_browser',
      inAppAlertsAvailable,
      isStandalonePwa: false,
    }
  }

  const messaging = await getMessagingIfSupported()
  if (!messaging || !isNotificationSupported()) {
    return {
      canEnablePush: false,
      reason: isIosDevice() ? 'ios_safari_browser' : 'unsupported',
      inAppAlertsAvailable,
      isStandalonePwa: standalone,
    }
  }

  return {
    canEnablePush: true,
    reason: null,
    inAppAlertsAvailable,
    isStandalonePwa: standalone,
  }
}
